import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Edit2, Trash2, Loader2 } from "lucide-react";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminVisaTypes = () => {
  const navigate = useNavigate();
  const [visaTypes, setVisaTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    value: "",
    label: "",
    is_renewal_type: false,
  });

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchVisaTypes();
  }, [token]);

  const fetchVisaTypes = async () => {
    try {
      const response = await axios.get(`${API}/visa-types?active_only=false`);
      setVisaTypes(response.data);
    } catch (error) {
      console.error("Error fetching visa types:", error);
      toast.error("Failed to load visa types");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (editingType) {
        await axios.put(`${API}/visa-types/${editingType.id}`, formData, { headers });
        toast.success("Visa type updated");
      } else {
        await axios.post(`${API}/visa-types`, formData, { headers });
        toast.success("Visa type created");
      }
      setDialogOpen(false);
      setEditingType(null);
      setFormData({ value: "", label: "", is_renewal_type: false });
      fetchVisaTypes();
    } catch (error) {
      console.error("Error saving visa type:", error);
      toast.error(error.response?.data?.detail || "Failed to save visa type");
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      value: type.value,
      label: type.label,
      is_renewal_type: type.is_renewal_type,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (typeId) => {
    if (!confirm("Are you sure you want to deactivate this visa type?")) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API}/visa-types/${typeId}`, { headers });
      toast.success("Visa type deactivated");
      fetchVisaTypes();
    } catch (error) {
      console.error("Error deleting visa type:", error);
      toast.error("Failed to deactivate visa type");
    }
  };

  const handleActivate = async (type) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.put(`${API}/visa-types/${type.id}`, {
        ...type,
        is_active: true
      }, { headers });
      toast.success("Visa type activated");
      fetchVisaTypes();
    } catch (error) {
      console.error("Error activating visa type:", error);
      toast.error("Failed to activate visa type");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-lg font-bold text-slate-900">Visa Type Management</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setEditingType(null);
                  setFormData({ value: "", label: "", is_renewal_type: false });
                }}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="add-visa-type-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Visa Type
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingType ? "Edit Visa Type" : "Add New Visa Type"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="value">Value (unique identifier)</Label>
                  <Input
                    id="value"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="e.g., 90_days_single"
                    required
                    disabled={!!editingType}
                    data-testid="visa-type-value-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Cannot be changed after creation</p>
                </div>
                <div>
                  <Label htmlFor="label">Display Name</Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="e.g., 90 Days Single Entry"
                    required
                    data-testid="visa-type-label-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_renewal_type"
                    checked={formData.is_renewal_type}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_renewal_type: checked })}
                    data-testid="visa-type-renewal-checkbox"
                  />
                  <Label htmlFor="is_renewal_type" className="cursor-pointer">
                    Requires deportation ticket declaration
                  </Label>
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="save-visa-type-btn">
                  {editingType ? "Update" : "Create"} Visa Type
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="flex-1 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <table className="data-table">
              <thead>
                <tr className="bg-slate-50">
                  <th>Value</th>
                  <th>Display Name</th>
                  <th>Deportation Declaration</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visaTypes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-slate-500">
                      No visa types found
                    </td>
                  </tr>
                ) : (
                  visaTypes.map((type) => (
                    <tr key={type.id}>
                      <td className="font-mono text-sm">{type.value}</td>
                      <td className="font-medium">{type.label}</td>
                      <td>
                        {type.is_renewal_type ? (
                          <span className="text-amber-600">Yes</span>
                        ) : (
                          <span className="text-slate-400">No</span>
                        )}
                      </td>
                      <td>
                        {type.is_active ? (
                          <span className="status-badge active">Active</span>
                        ) : (
                          <span className="status-badge pending">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(type)}
                            data-testid={`edit-${type.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {type.is_active ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(type.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-${type.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleActivate(type)}
                              className="text-green-600 hover:text-green-700"
                              data-testid={`activate-${type.id}`}
                            >
                              Activate
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminVisaTypes;
